import type { UpdateSchemaAttrObject } from './BodySchemaEditor/utils';
import type { EndpointDataType } from '@readme/api/mappings/page/reference/types';
import type { HttpMethods, OASDocument, OperationObject, PathsObject } from 'oas/types';
import type { OpenAPIV3 } from 'openapi-types';

import { Operation } from 'oas/operation';

import { actionLog, createBoundedUseStore, isClient } from '@core/store/util';

import { createCrossFrameStore } from '../util/createCrossFrameStore';

import { createBodySchemaEditorSlice, type BodySchemaEditorSlice } from './BodySchemaEditor';
import { getParameterSchema } from './BodySchemaEditor/utils';
import { createRequestExampleEditorSlice, type RequestExampleEditorSlice } from './RequestExampleEditor';
import { createResponseExampleEditorSlice, type ResponseExampleEditorSlice } from './ResponseExampleEditor';
import {
  convertToType,
  ensurePathHasLeadingSlash,
  moveParameter,
  getValidatedAPIDesignerState,
  generateUniqueParameterName,
  isAutoGeneratedOperationId,
} from './util';

export interface APIv2ReferencePayload extends EndpointDataType {
  schema?: OASDocument;
}

interface APIDesignerStoreState {
  /**
   * The api information that comes from APIv2. Includes spec, as well as the current path and method.
   */
  apiObject?: APIv2ReferencePayload;

  /**
   * Errors that occur when validating the API Designer form components.
   */
  errors: Record<string, string>;

  /**
   * Indicates whether the reference store is fully hydrated with incoming data
   * either from our SSR props, API endpoint or some other call to reset() that
   * moves us out of our "default" starting state.
   */
  isReady: boolean;

  /**
   * The recently used media types.
   * This is a LIFO queue of the last 3 media types used.
   * The most recently used media type is at the beginning of the array.
   * The least recently used media type is at the end of the array.
   * Note: we use a string here because customers may define custom media types
   */
  recentlyUsedMediaTypes: string[];
}

interface APIDesignerStoreActions {
  /**
   * Adds a new parameter to the current endpoint
   */
  addParameter: (location: Exclude<OpenAPIV3.ParameterObject['in'], 'cookie'>) => void;

  /**
   * Adds a new security scheme to the current operation
   * Can create a new security scheme group or add to an existing one
   */
  addSecurityScheme: (scheme: string, index?: number) => void;

  /**
   * Removes the error message for a specific field. If no field is provided, all
   * errors are cleared.
   */
  clearErrors: (field?: string) => void;

  /** Creates a new operation in the OAS file */
  createOperation: () => void;

  /**
   * Returns the available security schemes for the current OAS file
   */
  getAvailableSecuritySchemes: () => Record<string, OpenAPIV3.SecuritySchemeObject>;

  /**
   * Returns the operation on the OAS file for the current path and method
   */
  getCurrentOperation: () => {
    currentMethod: HttpMethods;
    currentPath: string;
    operation: OpenAPIV3.OperationObject | undefined;
  };

  /**
   * Returns the endpoint security schemes for the current operation
   */
  getEndpointSecuritySchemes: () => OpenAPIV3.SecurityRequirementObject[];

  /**
   * Returns the global security schemes for the current OAS file
   */
  getGlobalSecuritySchemes: () => OpenAPIV3.SecurityRequirementObject[];

  /**
   * Returns the scopes for a given security scheme and index
   */
  getOperationSecuritySchemeScopes: (scheme: string, index: number) => string[];

  /**
   * Initializes the API Designer store. Used by `InitializeAPIDesignerStore`.
   */
  initialize: (opts: { apiObject: APIv2ReferencePayload }) => void;

  /**
   * Removes a parameter from the current endpoint.
   */
  removeParameter: (location: Exclude<OpenAPIV3.ParameterObject['in'], 'cookie'>, name: string) => void;

  /**
   * Removes a security scheme from the current operation
   * Can remove a specific security scheme within a group or the entire group
   */
  removeSecurityScheme: (scheme: string, index: number) => void;

  /**
   * Resets state back to the last "initialized" state. When `partialState` is
   * provided, it is merged into state and re-initialized to this.
   */
  reset: () => void;

  /**
   * Updates the current API object.
   */
  setApiObject: (apiObject: APIv2ReferencePayload) => void;

  /**
   * Updates the errors object with a new error message.
   */
  setError: (field: string, message: string) => void;

  /**
   * Updates the method of an operation.
   */
  setMethod: (newMethod: HttpMethods) => void;

  /**
   * Updates the current OpenAPI definition within the API object.
   */
  setOas: (oas: OASDocument) => void;

  /**
   * Updates the path of an operation.
   */
  setPath: (newPath: string) => void;

  /**
   * Updates the summary for an operation.
   */
  setSummary: (newSummary: string) => void;

  /**
   * Toggles an OAuth scope for a given security scheme.
   */
  toggleOAuthScope: (scheme: string, scope: string, index: number) => void;

  /**
   * Batch toggles multiple OAuth scopes for a given security scheme.
   */
  toggleOAuthScopesBatch: (scheme: string, scopesToToggle: string[], index: number) => void;

  /**
   * Updates a parameter object in the current endpoint.
   */
  updateParameter: (
    location: Exclude<OpenAPIV3.ParameterObject['in'], 'cookie'>,
    index: number,
    action: UpdateSchemaAttrObject,
  ) => void;
}

export type APIDesignerStore = APIDesignerStoreActions &
  APIDesignerStoreState &
  BodySchemaEditorSlice &
  RequestExampleEditorSlice &
  ResponseExampleEditorSlice;

const initialState: APIDesignerStoreState = {
  isReady: false,
  errors: {},
  recentlyUsedMediaTypes: [],
};

export const apiDesignerStore = createCrossFrameStore<APIDesignerStore>('APIDesignerStore', (set, get, ...props) => {
  /**
   * Holds reference to the initial state so we can support resetting the
   * store back to this state when calling `reset()`.
   */
  const resetState = {
    ...initialState,
    ...createBodySchemaEditorSlice(set, get, ...props),
    ...createRequestExampleEditorSlice(set, get, ...props),
    ...createResponseExampleEditorSlice(set, get, ...props),
  };

  return {
    ...resetState,

    initialize: ({ apiObject }) => {
      get().setApiObject(apiObject);
      get().requestExampleEditor.initialize();
      get().responseExampleEditor.initialize();

      // The createDefaultReferencePage() function will set the api object with
      // an empty string as the placeholder path value. If the API Designer
      // initializes with this empty path, it tells us we need to populate the
      // current state with a new operation.
      if (apiObject?.path === '') {
        get().createOperation();
      }

      set(
        state => {
          // When running on the server, we must avoid marking this store
          // as "ready" to ensure it continues receiving updates until it
          // gets initialized on the client's first render.
          if (isClient) {
            state.isReady = true;
          }
        },
        false,
        actionLog('initialize', apiObject),
      );
    },

    addParameter: location => {
      set(
        state => {
          const { operation } = getValidatedAPIDesignerState(state);
          if (!operation.parameters) {
            operation.parameters = [];
          }

          // Get existing parameter names in the same location
          const existingParameterNames = operation.parameters
            .filter((p): p is OpenAPIV3.ParameterObject => !('$ref' in p) && p.in === location)
            .map(p => p.name)
            .filter(Boolean);

          // Generate a unique name for the new parameter
          const uniqueName = generateUniqueParameterName(existingParameterNames);

          const newSchema: OpenAPIV3.ParameterObject = {
            in: location,
            name: uniqueName,
            schema: { type: 'string' },
          };
          operation.parameters.push(newSchema);
        },
        false,
        actionLog('addParameter', { location }),
      );
    },

    getCurrentOperation: () => {
      const oas = get().apiObject?.schema;
      const currentPath = get().apiObject?.path as string;
      const currentMethod = get().apiObject?.method as HttpMethods;
      const operation = oas?.paths?.[currentPath]?.[currentMethod];

      return { currentMethod, currentPath, operation };
    },

    removeParameter(location, name) {
      set(
        state => {
          const { operation } = getValidatedAPIDesignerState(state);
          const filteredParameters = operation.parameters?.filter(p => {
            if ('$ref' in p) return true;
            return p.in !== location || p.name !== name;
          });

          operation.parameters = filteredParameters;
        },
        false,
        actionLog('removeParameter', { location, name }),
      );
    },

    reset: () => {
      set(resetState, false, actionLog('reset'));
    },

    setApiObject: apiObject => {
      set(
        state => {
          if (!apiObject) {
            state.apiObject = apiObject;
            return;
          }

          // Normalize paths to have leading slash per OpenAPI spec (fixes existing data).
          // Webhook paths don't use leading slashes, so skip normalization for those.
          // see https://spec.openapis.org/oas/v3.1.0.html#patterned-fields
          let normalizedApiObject = apiObject;
          const schema = apiObject.schema;
          const isWebhookPath = !!(schema as Record<string, unknown>)?.webhooks?.[apiObject.path as string];
          if (!isWebhookPath && schema?.paths && Object.keys(schema.paths).some(key => !key.startsWith('/'))) {
            const newPaths: PathsObject = {};
            Object.entries(schema.paths).forEach(([key, value]) => {
              newPaths[ensurePathHasLeadingSlash(key)] = value;
            });

            normalizedApiObject = {
              ...apiObject,
              schema: { ...schema, paths: newPaths } as OASDocument,
              path: ensurePathHasLeadingSlash(apiObject.path),
            };
          } else if (!isWebhookPath && apiObject.path && !apiObject.path.startsWith('/')) {
            normalizedApiObject = {
              ...apiObject,
              path: ensurePathHasLeadingSlash(apiObject.path),
            };
          }

          state.apiObject = normalizedApiObject;
        },
        false,
        actionLog('setApiObject', { apiObject }),
      );
    },

    setOas: oas => {
      set(
        state => {
          if (!state.apiObject) throw new Error('apiObject is required');
          state.apiObject.schema = oas;
        },
        false,
        actionLog('setOas', { oas }),
      );
    },

    setMethod: newMethod => {
      set(
        state => {
          const { apiObject, paths, path, method } = getValidatedAPIDesignerState(state);

          paths[path]![newMethod] = paths[path]![method];
          delete paths[path]![method];

          // When the HTTP method changes, the operationId needs to be regenerated only if
          // it was auto-generated by the API Designer. We check if the current operationId
          // matches the auto-generated pattern before regenerating it.
          const operation = paths[path]![newMethod] as OperationObject;
          const currentOperationId = operation.operationId;

          if (isAutoGeneratedOperationId(currentOperationId, path, method)) {
            // Only regenerate if it was auto-generated
            delete operation.operationId;
            operation.operationId = Operation.getOperationId(path, newMethod, operation);
          }

          apiObject.method = newMethod;
        },
        false,
        actionLog('setMethod', { newMethod }),
      );
    },

    setSummary: newSummary => {
      set(
        state => {
          const { operation } = getValidatedAPIDesignerState(state);

          operation.summary = newSummary;
        },
        false,
        actionLog('setSummary', { newSummary }),
      );
    },

    setPath: newPath => {
      // Normalize the path to ensusure it has a leading slash per the OpenAPI specification.
      const normalizedPath = ensurePathHasLeadingSlash(newPath);
      set(
        state => {
          const { apiObject, paths, path, method, operation } = getValidatedAPIDesignerState(state);

          // Update the current path in the API object
          apiObject.path = normalizedPath;

          // Update the new path in the schema
          if (!paths[normalizedPath]) paths[normalizedPath] = {};
          paths[normalizedPath][method] = operation;

          // When the path changes, the operationId needs to be regenerated only if
          // it was auto-generated by the API Designer. We check if the current operationId
          // matches the auto-generated pattern before regenerating it.
          const operationAtNewPath = paths[normalizedPath][method] as OperationObject;
          const currentOperationId = operationAtNewPath.operationId;

          if (isAutoGeneratedOperationId(currentOperationId, path, method)) {
            // Only regenerate if it was auto-generated
            delete operationAtNewPath.operationId;
            operationAtNewPath.operationId = Operation.getOperationId(normalizedPath, method, operationAtNewPath);
          }

          // Delete the old operation
          if (paths[path] && paths[path][method]) {
            delete paths[path][method];
          }

          // This was the only operation in the path
          if (paths[path] && Object.keys(paths[path]).length === 0) {
            delete paths[path];
          }

          let parameters = paths[normalizedPath][method].parameters;
          // If there aren't any parameters yet, this won't be in the object
          if (!parameters) {
            parameters = [];
          }

          // Make sure newly added path params are added to the spec
          const expectedPathParams = Array.from(normalizedPath.matchAll(/({([^}]+)})/g), m => m[2]);
          const missingPathParams = expectedPathParams
            .map(param => {
              const pathParam = parameters.find(p => 'name' in p && p.name === param);
              if (!pathParam) {
                return { in: 'path', name: param, schema: { type: 'string' }, required: true };
              }
              return false;
            })
            .filter(Boolean) as OpenAPIV3.ParameterObject[];

          // Only want to add parameters if there are some missing
          if (missingPathParams.length > 0) {
            paths[normalizedPath][method].parameters = [...parameters, ...missingPathParams];
          } else if (parameters.length > 0) {
            paths[normalizedPath][method].parameters = parameters;
          }

          if (paths[normalizedPath][method].parameters) {
            // Remove path params that aren't in the path anymore
            paths[normalizedPath][method].parameters = paths[normalizedPath][method].parameters.filter(
              p =>
                '$ref' in p ||
                p.in !== 'path' ||
                expectedPathParams.some(expectedName => p.name === expectedName && p.in === 'path'),
            );
          }
        },
        false,
        actionLog('setPath', { newPath: normalizedPath }),
      );
    },

    updateParameter(location, index, action) {
      set(
        state => {
          const { operation } = getValidatedAPIDesignerState(state);

          const parameters = (operation.parameters || []).filter(param => 'in' in param && param.in === location);

          const currentParameter = parameters[index];

          if (!currentParameter) {
            throw new Error(`Parameter at index ${index} does not exist`);
          }

          if ('$ref' in currentParameter) {
            throw new Error(`Cannot update parameter at index ${index} because it is a reference`);
          }

          // Get schema from either schema field or content field
          const currentParameterSchema = getParameterSchema(currentParameter);
          if (!currentParameterSchema) {
            throw new Error(
              `Cannot update parameter at index ${index} because it does not have a valid schema or contains a reference`,
            );
          }

          const isContentBased = 'content' in currentParameter && currentParameter.content;

          switch (action.attribute) {
            case 'default':
              currentParameterSchema.default = action.value;
              break;
            case 'enum':
              switch (action.value.action) {
                case 'push':
                  if (!currentParameterSchema.enum) {
                    currentParameterSchema.enum = [];
                  }
                  if (!currentParameterSchema.enum.includes(action.value.value)) {
                    currentParameterSchema.enum.push(action.value.value);
                  }
                  break;
                case 'remove':
                  if (
                    !currentParameterSchema.enum ||
                    action.value.value < 0 ||
                    action.value.value >= currentParameterSchema.enum.length
                  ) {
                    throw new Error(`Enum at index ${action.value.value} does not exist`);
                  }
                  currentParameterSchema.enum.splice(action.value.value, 1);
                  if (currentParameterSchema.enum.length === 0) {
                    delete currentParameterSchema.enum;
                  }
                  break;
                case 'clear':
                  delete currentParameterSchema.enum;
                  break;
                default:
                  throw new Error('Invalid enum action');
              }
              break;
            case 'required':
              currentParameter.required = action.value;
              break;
            case 'type':
              // eslint-disable-next-line no-case-declarations
              const previousType = currentParameterSchema.type;
              currentParameterSchema.type = action.value.type;

              // set a format if it exists, otherwise delete it
              if (action.value.format) {
                currentParameterSchema.format = action.value.format;
              } else {
                delete currentParameterSchema.format;
              }

              // if the type is an object and there are no properties, create an empty properties object
              // otherwise, remove the `properties` and `required` fields
              if (action.value.type === 'object') {
                if (!currentParameterSchema.properties) currentParameterSchema.properties = {};
              } else {
                delete currentParameterSchema.properties;
                delete currentParameterSchema.required;
              }

              // if the type is an array, set the items field
              // otherwise, remove the items field
              // eslint-disable-next-line no-case-declarations
              let isArrayOfObject = false;
              if (action.value.type === 'array' && action.value.items) {
                (currentParameterSchema as OpenAPIV3.ArraySchemaObject).items = action.value.items;

                // special case for query parameter to serialize object
                // Note: For content-based parameters, style/explode are ignored per OAS spec
                if (
                  !isContentBased &&
                  location === 'query' &&
                  'type' in action.value.items &&
                  action.value.items.type === 'object'
                ) {
                  currentParameter.style = 'deepObject';
                  currentParameter.explode = true;
                  isArrayOfObject = true;
                }
              } else {
                // @ts-expect-error we want to delete this property whether or not it exists
                delete currentParameterSchema.items;
              }

              // undo style settings if we change from an array of object to something else
              // Note: For content-based parameters, style/explode are ignored per OAS spec
              if (!isContentBased && !isArrayOfObject && previousType === 'array') {
                if (currentParameter.style === 'deepObject') {
                  delete currentParameter.style;
                }
                if (currentParameter.explode !== undefined) {
                  delete currentParameter.explode;
                }
              }

              // try to ensure the enum values are of the correct type when in View mode
              if (
                previousType !== action.value.type &&
                currentParameterSchema.enum &&
                currentParameterSchema.enum.length > 0
              ) {
                currentParameterSchema.enum = currentParameterSchema.enum.map(value =>
                  convertToType(value, action.value.type),
                );
              }
              break;
            case 'move':
              moveParameter(parameters, operation.parameters || [], action.value);
              break;
            default:
              currentParameter[action.attribute] = action.value;
          }
        },
        false,
        actionLog('updateParameter', { index, location, action }),
      );
    },

    createOperation: () => {
      set(
        state => {
          if (!state.apiObject) throw new Error('apiObject is required');
          if (!state.apiObject.schema) throw new Error('schema is required');
          if (!state.apiObject.schema.paths) {
            state.apiObject.schema.paths = {};
          }
          let newEndpointPath = '/new-endpoint';
          let i = 1;
          while (state.apiObject.schema.paths[newEndpointPath]?.get) {
            newEndpointPath = `/new-endpoint-${i}`;
            i += 1;
          }
          state.apiObject.path = newEndpointPath;
          state.apiObject.method = 'get';
          const newOperation = {
            get: {
              description: '',
              operationId: '',
              responses: {
                200: {
                  description: '',
                },
              },
              parameters: [],
            },
          };
          if (!state.apiObject?.schema?.paths) {
            state.apiObject.schema = { ...state.apiObject.schema, paths: { [newEndpointPath]: newOperation } };
          } else if (state.apiObject.schema.paths[newEndpointPath]) {
            state.apiObject.schema.paths[newEndpointPath]!.get = newOperation.get;
          } else {
            state.apiObject.schema.paths = { ...state.apiObject.schema.paths, [newEndpointPath]: newOperation };
          }
        },
        false,
        actionLog('createOperation'),
      );
    },

    setError: (field, message) => {
      set(
        state => {
          state.errors[field] = message;
        },
        false,
        actionLog('setError', { field, message }),
      );
    },

    clearErrors: field => {
      set(
        state => {
          if (field) {
            delete state.errors[field];
          } else {
            state.errors = {};
          }
        },
        false,
        actionLog('clearError', { field }),
      );
    },

    getAvailableSecuritySchemes: () => {
      const oas = get().apiObject?.schema;
      return (oas?.components?.securitySchemes as Record<string, OpenAPIV3.SecuritySchemeObject>) || {};
    },

    getGlobalSecuritySchemes: () => {
      const oas = get().apiObject?.schema;
      return (oas?.security as OpenAPIV3.SecurityRequirementObject[]) || [];
    },

    getEndpointSecuritySchemes: () => {
      const { operation } = getValidatedAPIDesignerState(get());
      return (operation.security as OpenAPIV3.SecurityRequirementObject[]) || [];
    },

    addSecurityScheme: (scheme: string, index?: number) => {
      set(state => {
        const { operation } = getValidatedAPIDesignerState(state);
        if (!operation.security) {
          operation.security = [];
        }

        if (index !== undefined) {
          // case 1: adding to an existing security scheme group at specific index
          if (index < 0 || index >= operation.security.length) {
            throw new Error(`Invalid security group index: ${index}`);
          }
          operation.security[index][scheme] = [];
        } else {
          // case 2: creating a new security scheme group
          operation.security.push({ [scheme]: [] });
        }
      });
    },

    removeSecurityScheme: (scheme: string, index: number) => {
      set(state => {
        const { operation } = getValidatedAPIDesignerState(state);
        if (!operation.security) {
          operation.security = [];
        }

        if (index < 0 || index >= operation.security.length) {
          throw new Error(`Invalid security group index: ${index}`);
        }

        const securityGroup = operation.security[index];
        delete securityGroup[scheme];

        // If the group is now empty, remove it entirely
        if (Object.keys(securityGroup).length === 0) {
          operation.security.splice(index, 1);
        }

        // if the operation.security is empty, delete it
        if (operation.security.length === 0) {
          delete operation.security;
        }
      });
    },

    toggleOAuthScope: (scheme: string, scope: string, index: number) => {
      set(state => {
        const { operation } = getValidatedAPIDesignerState(state);
        if (!operation.security) {
          operation.security = [];
        }

        if (index < 0 || index >= operation.security.length) {
          throw new Error(`Invalid security group index: ${index}`);
        }

        const securityGroup = operation.security[index];
        if (securityGroup[scheme]) {
          const scopes = securityGroup[scheme];
          if (scopes.includes(scope)) {
            securityGroup[scheme] = scopes.filter(s => s !== scope);
          } else {
            securityGroup[scheme] = [...scopes, scope];
          }
        } else {
          securityGroup[scheme] = [scope];
        }
      });
    },

    toggleOAuthScopesBatch: (scheme: string, scopesToToggle: string[], index: number) => {
      set(state => {
        const { operation } = getValidatedAPIDesignerState(state);
        if (!operation.security) {
          operation.security = [];
        }

        if (index < 0 || index >= operation.security.length) {
          throw new Error(`Invalid security group index: ${index}`);
        }

        const securityGroup = operation.security[index];
        const currentScopes = [...(securityGroup[scheme] || [])];

        scopesToToggle.forEach(scope => {
          if (currentScopes.includes(scope)) {
            const scopeIndex = currentScopes.indexOf(scope);
            currentScopes.splice(scopeIndex, 1);
          } else {
            currentScopes.push(scope);
          }
        });

        securityGroup[scheme] = currentScopes;
      });
    },

    getOperationSecuritySchemeScopes: (scheme: string, index: number) => {
      const { operation } = getValidatedAPIDesignerState(get());
      if (!operation.security) {
        return [];
      }
      return operation.security[index][scheme] || [];
    },
  };
});

export const useAPIDesignerStore = createBoundedUseStore(apiDesignerStore);

export * from './InitializeAPIDesignerStore';
